import { useState } from 'react';
import { EmptyState } from '@pazportal/ui';
import { useCustomer } from '../hooks/useCustomer';
import { useCustomerInvoices, type CustomerInvoice } from '../hooks/useCustomerInvoices';
import { useAccount } from '../contexts/AccountContext';
import { SkeletonCard, SkeletonRow } from '../components/Skeleton';
import { StatusBadge } from '../components/StatusBadge';
import { AccountSelect } from '../components/AccountSelect';
import { useI18n } from '../i18n/I18nProvider';
import { formatCurrency, formatDate, daysBetween } from '../lib/formatters';

/**
 * Contract terms and invoices. Billing is inherently per account, so in
 * portfolio mode the page shows one chosen account at a time. Every figure
 * comes from the customers row / invoices_customer_view; anything that is
 * not on file is labelled as such rather than defaulted.
 */
export function BillingPage() {
  const { t } = useI18n();
  const { accounts, activeAccount, isPortfolio, ready } = useAccount();
  const [chosen, setChosen] = useState<string | null>(null);

  const customerId = isPortfolio
    ? chosen && accounts.some((a) => a.customer_id === chosen)
      ? chosen
      : accounts[0]?.customer_id ?? null
    : activeAccount?.customer_id ?? null;

  const { data: customer, isLoading, isError, error } = useCustomer(customerId);
  const invoices = useCustomerInvoices(customerId);

  const header = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <h1 className="text-xl font-semibold text-text">{t('billing.title')}</h1>
      {isPortfolio && (
        <AccountSelect value={customerId} onChange={setChosen} label={t('billing.choose_account')} />
      )}
    </div>
  );

  if (!ready || (customerId && isLoading)) {
    return (
      <div className="space-y-6">
        {header}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="rounded-lg border border-border bg-surface p-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="space-y-4">
        {header}
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState icon={<CardIcon />} title={t('account.none')} subtitle={t('account.none_hint')} />
        </div>
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="space-y-4">
        {header}
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState
            icon={<CardIcon />}
            title={t('billing.no_info')}
            subtitle={isError && error instanceof Error ? error.message : t('billing.no_info_hint')}
          />
        </div>
      </div>
    );
  }

  const mrr = customer.mrr_cents ?? 0;
  const hasValue = mrr > 0;
  const renewalDays = customer.contract_renewal_date
    ? daysBetween(new Date().toISOString(), customer.contract_renewal_date)
    : null;
  const renewalTone =
    renewalDays === null
      ? 'text-text'
      : renewalDays < 0
        ? 'text-notification'
        : renewalDays < 30
          ? 'text-notification'
          : renewalDays < 60
            ? 'text-warning'
            : 'text-success';

  return (
    <div className="space-y-6">
      {header}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
          <p className="text-sm text-text-muted">{t('billing.annual_value')}</p>
          {hasValue ? (
            <>
              <p className="mt-1 text-xl font-semibold text-text sm:text-2xl">
                {formatCurrency(mrr * 12)}
              </p>
              <p className="mt-1 text-xs text-text-faint">
                {t('billing.per_month', { amount: formatCurrency(mrr) })}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm font-medium text-text">{t('billing.no_value')}</p>
          )}
        </div>
        <div className="rounded-lg border border-border bg-surface p-4 sm:p-5">
          <p className="text-sm text-text-muted">{t('billing.renewal')}</p>
          {renewalDays !== null ? (
            <>
              <p className={`mt-1 text-xl font-semibold sm:text-2xl ${renewalTone}`}>
                {renewalDays < 0
                  ? t('dashboard.renewal_past')
                  : t('dashboard.renewal_days', { n: renewalDays })}
              </p>
              <p className="mt-1 text-xs text-text-faint">
                {formatDate(customer.contract_renewal_date)}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm font-medium text-text">{t('billing.no_renewal')}</p>
          )}
        </div>
        <div className="rounded-lg border border-border bg-surface p-4 sm:col-span-2 sm:p-5 md:col-span-1">
          <p className="text-sm text-text-muted">{t('billing.sla_tier')}</p>
          <div className="mt-2">
            {customer.sla_tier ? (
              <StatusBadge status={customer.sla_tier} className="px-3 py-1 text-sm" />
            ) : (
              <p className="text-sm font-medium text-text">{t('billing.not_set')}</p>
            )}
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-border bg-surface p-4 sm:p-5" aria-labelledby="contract-heading">
        <h2 id="contract-heading" className="mb-4 text-sm font-medium text-text-muted">
          {t('billing.contract')}
        </h2>
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-4">
            <dt className="flex-shrink-0 text-text-muted">{t('billing.account_number')}</dt>
            <dd className="truncate font-mono text-xs text-text">{customer.account_number}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="flex-shrink-0 text-text-muted">{t('billing.sla_tier')}</dt>
            <dd>
              {customer.sla_tier ? (
                <StatusBadge status={customer.sla_tier} />
              ) : (
                <span className="text-text">{t('billing.not_set')}</span>
              )}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="flex-shrink-0 text-text-muted">{t('billing.cycle')}</dt>
            <dd className="font-medium capitalize text-text">
              {customer.billing_cycle ?? t('billing.not_set')}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="flex-shrink-0 text-text-muted">{t('billing.renewal')}</dt>
            <dd className="font-medium text-text">
              {customer.contract_renewal_date
                ? formatDate(customer.contract_renewal_date)
                : t('billing.no_renewal')}
            </dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-border bg-surface" aria-labelledby="invoices-heading">
        <div className="border-b border-border px-4 py-4 sm:px-5">
          <h2 id="invoices-heading" className="text-sm font-medium text-text-muted">
            {t('billing.invoices')}
          </h2>
        </div>
        {invoices.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : invoices.isError ? (
          <p role="alert" className="p-5 text-sm text-notification">
            {t('billing.invoices_error')}{' '}
            {invoices.error instanceof Error ? invoices.error.message : ''}
          </p>
        ) : (invoices.data ?? []).length === 0 ? (
          <p className="p-6 text-center text-sm text-text-muted">{t('billing.invoices_empty')}</p>
        ) : (
          <>
            <ul className="divide-y divide-divider md:hidden">
              {(invoices.data ?? []).map((inv) => (
                <li key={inv.id} className="space-y-1 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-text">{inv.number ?? t('billing.invoice')}</p>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="text-sm text-text">{formatCurrency(inv.amount_due_cents)}</p>
                  <p className="text-xs text-text-faint">
                    {inv.issued_at ? formatDate(inv.issued_at) : t('common.none')}
                  </p>
                  <InvoiceLinks invoice={inv} />
                </li>
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-text-muted">
                    <th scope="col" className="px-5 py-3 font-medium">{t('billing.invoice')}</th>
                    <th scope="col" className="px-5 py-3 font-medium">{t('billing.date')}</th>
                    <th scope="col" className="px-5 py-3 font-medium">{t('billing.amount')}</th>
                    <th scope="col" className="px-5 py-3 font-medium">{t('billing.status')}</th>
                    <th scope="col" className="px-5 py-3">
                      <span className="sr-only">{t('billing.view')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {(invoices.data ?? []).map((inv) => (
                    <tr key={inv.id} className="text-text">
                      <td className="px-5 py-3">{inv.number ?? t('billing.invoice')}</td>
                      <td className="px-5 py-3">
                        {inv.issued_at ? formatDate(inv.issued_at) : t('common.none')}
                      </td>
                      <td className="px-5 py-3">{formatCurrency(inv.amount_due_cents)}</td>
                      <td className="px-5 py-3">
                        <StatusBadge status={inv.status} />
                      </td>
                      <td className="px-5 py-3 text-right">
                        <InvoiceLinks invoice={inv} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <p className="py-2 text-center text-xs text-text-faint">{t('billing.footer')}</p>
    </div>
  );
}

function InvoiceLinks({ invoice }: { invoice: CustomerInvoice }) {
  const { t } = useI18n();
  if (!invoice.hosted_invoice_url && !invoice.invoice_pdf_url) return null;
  return (
    <span className="inline-flex flex-wrap gap-x-3 gap-y-1">
      {invoice.hosted_invoice_url && (
        <a
          href={invoice.hosted_invoice_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center text-xs font-medium text-primary hover:underline md:min-h-0"
        >
          {invoice.status === 'open' ? t('billing.pay_now') : t('billing.view')}
          <span className="sr-only"> ({t('common.new_tab')})</span>
        </a>
      )}
      {invoice.invoice_pdf_url && (
        <a
          href={invoice.invoice_pdf_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 items-center text-xs font-medium text-primary hover:underline md:min-h-0"
        >
          {t('billing.pdf')}
          <span className="sr-only"> ({t('common.new_tab')})</span>
        </a>
      )}
    </span>
  );
}

function CardIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 10h18M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
      />
    </svg>
  );
}
